import React from 'react'
import {
  Step,
  initializeSteps as initializeStepsGeneric,
} from '@/shared/components/cl-chat/steps-setup'

// Steps demonstrating MDX custom components
export const STEPS: Step[] = [
  {
    type: 'userText',
    content: 'How can I use custom React components in my MDX documentation?',
  },
  {
    type: 'systemText',
    showProcessing: {
      mode: 'dots',
      duration: 1500,
    },
    content:
      'Great question! MDX lets you seamlessly mix Markdown with JSX. You can import and use any React component directly in your .mdx files.',
  },
  {
    type: 'code',
    content: [
      // Line 1
      <>
        <span className="text-gray-500">{'// Example.mdx'}</span>
      </>,
      // Line 2
      <>
        <span className="text-purple-400">import</span>
        <span className="text-white"> {'{ Note }'} </span>
        <span className="text-purple-400">from</span>
        <span className="text-green-300"> {"'@/components/Note'"}</span>
      </>,
      // Line 3
      <></>,
      // Line 4
      <>
        <span className="text-gray-400">{'## My Documentation'}</span>
      </>,
      // Line 5
      <></>,
      // Line 6
      <>
        <span className="text-white">Regular markdown text here.</span>
      </>,
      // Line 7
      <></>,
      // Line 8
      <>
        <span className="text-blue-300">{'<Note>'}</span>
      </>,
      // Line 9
      <>
        <span className="ml-4 text-white"> This is a custom component!</span>
      </>,
      // Line 10
      <>
        <span className="text-blue-300">{'</Note>'}</span>
      </>,
    ],
    highlightedLines: [2, 8, 9, 10],
    highlightColor: 'blue',
  },
  {
    type: 'userText',
    content: 'Can I pass props to these components?',
  },
  {
    type: 'systemText',
    showProcessing: {
      mode: 'dots',
      duration: 1500,
    },
    content: 'Absolutely! You can pass props just like in regular JSX:',
  },
  {
    type: 'code',
    content: [
      // Line 1
      <>
        <span className="text-blue-300">{'<Note'}</span>
        <span className="text-yellow-300"> type</span>
        <span className="text-white">=</span>
        <span className="text-green-300">&quot;warning&quot;</span>
        <span className="text-blue-300">{'>'}</span>
      </>,
      // Line 2
      <>
        <span className="ml-4 text-white"> Be careful with this!</span>
      </>,
      // Line 3
      <>
        <span className="text-blue-300">{'</Note>'}</span>
      </>,
      // Line 4
      <></>,
      // Line 5
      <>
        <span className="text-blue-300">{'<CodeBlock'}</span>
      </>,
      // Line 6
      <>
        <span className="ml-4 text-yellow-300"> language</span>
        <span className="text-white">=</span>
        <span className="text-green-300">&quot;typescript&quot;</span>
      </>,
      // Line 7
      <>
        <span className="ml-4 text-yellow-300"> showLineNumbers</span>
        <span className="text-white">=</span>
        <span className="text-blue-400">{'{true}'}</span>
      </>,
      // Line 8
      <>
        <span className="ml-4 text-blue-300">{'>'}</span>
      </>,
      // Line 9
      <>
        <span className="ml-6 text-white"> console.log(&quot;Hello!&quot;)</span>
      </>,
      // Line 10
      <>
        <span className="text-blue-300">{'</CodeBlock>'}</span>
      </>,
    ],
    highlightedLines: [1, 2, 3, 5, 6, 7, 8, 9, 10],
    highlightColor: 'green',
  },
  {
    type: 'userText',
    content: 'This is perfect! Can you show me how to create a reusable layout?',
  },
  {
    type: 'systemText',
    showProcessing: {
      mode: 'dots',
      duration: 1500,
    },
    content: 'Sure! Here&apos;s an example of a two-column layout using Row and Col components:',
  },
  {
    type: 'code',
    content: [
      // Line 1
      <>
        <span className="text-blue-300">{'<Row>'}</span>
      </>,
      // Line 2
      <>
        <span className="ml-4 text-blue-300"> {'<Col>'}</span>
      </>,
      // Line 3
      <>
        <span className="ml-8 text-gray-400"> {'### Left Column'}</span>
      </>,
      // Line 4
      <>
        <span className="ml-8 text-white"> Content for the left side</span>
      </>,
      // Line 5
      <>
        <span className="ml-4 text-blue-300"> {'</Col>'}</span>
      </>,
      // Line 6
      <>
        <span className="ml-4 text-blue-300"> {'<Col>'}</span>
      </>,
      // Line 7
      <>
        <span className="ml-8 text-gray-400"> {'### Right Column'}</span>
      </>,
      // Line 8
      <>
        <span className="ml-8 text-white"> Content for the right side</span>
      </>,
      // Line 9
      <>
        <span className="ml-4 text-blue-300"> {'</Col>'}</span>
      </>,
      // Line 10
      <>
        <span className="text-blue-300">{'</Row>'}</span>
      </>,
    ],
    highlightedLines: [1, 2, 5, 6, 9, 10],
    highlightColor: 'blue',
  },
  {
    type: 'systemText',
    content:
      'This makes your documentation both powerful and beautiful! Mix markdown prose with interactive React components for the best of both worlds. 🎉',
  },
  {
    type: 'restart',
  },
]

// Function to initialize steps with dynamic IDs and callbacks
export const initializeSteps = (continueFromInteraction: (stepIndex: number) => void) => {
  initializeStepsGeneric(STEPS, continueFromInteraction, 'mdx-step')
}
